import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent } from './ui/dialog';
import { 
  Star, Quote, Play, ChevronLeft, ChevronRight, ArrowRight, 
  BadgeCheck, Users, Video, X, Instagram
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Extract YouTube video ID
const extractYoutubeId = (url) => {
  const match = url?.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]+)/);
  return match ? match[1] : null;
};

// Extract Instagram Reel ID
const extractInstagramId = (url) => {
  const match = url?.match(/instagram\.com\/(?:reel|p)\/([A-Za-z0-9_-]+)/);
  return match ? match[1] : null;
};

// Star Rating Component
const StarRating = ({ rating, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };
  
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star 
          key={i} 
          className={`${sizeClasses[size]} ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`} 
        />
      ))}
    </div>
  );
};

// Video Testimonial Card
const VideoTestimonialCard = ({ review, onClick }) => {
  const youtubeId = extractYoutubeId(review.video_url);
  const thumbnail = youtubeId 
    ? `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`
    : null;

  return (
    <div 
      className="relative aspect-[9/16] rounded-xl overflow-hidden cursor-pointer group bg-slate-800"
      onClick={onClick}
      data-testid={`video-testimonial-${review.id}`}
    >
      {thumbnail ? (
        <img 
          src={thumbnail} 
          alt={review.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            if (youtubeId) {
              e.target.src = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
            }
          }}
        />
      ) : review.video_type === 'instagram' ? (
        <div className="w-full h-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex flex-col items-center justify-center">
          <Instagram className="w-12 h-12 text-white mb-2" />
          <span className="text-white/80 text-sm">Video Testimonial</span>
        </div>
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-sky-600 to-sky-800 flex items-center justify-center">
          <Video className="w-12 h-12 text-white/50" />
        </div>
      )}
      
      {/* Play button overlay */}
      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
        <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center transform scale-90 group-hover:scale-100 transition-transform shadow-lg">
          <Play className="w-6 h-6 text-slate-800 ml-1" />
        </div>
      </div>
      
      {/* Customer info */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
        <StarRating rating={review.rating} size="sm" />
        <p className="text-white font-semibold mt-2">{review.name}</p>
        {review.position && (
          <p className="text-slate-300 text-xs">{review.position}</p>
        )}
      </div>
    </div>
  );
};

// Text Review Card
const TextReviewCard = ({ review }) => (
  <Card className="h-full hover:shadow-lg transition-shadow" data-testid={`text-review-${review.id}`}>
    <CardContent className="p-6 flex flex-col h-full">
      <Quote className="w-8 h-8 text-sky-200 mb-3" />
      <StarRating rating={review.rating} size="sm" />
      
      <p className="text-slate-600 mt-4 flex-grow leading-relaxed">
        "{review.short_quote || review.review}"
      </p>
      
      <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-100">
        {review.customer_image ? (
          <img 
            src={review.customer_image} 
            alt={review.name}
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-sky-600 rounded-full flex items-center justify-center text-white font-bold">
            {review.name.charAt(0)}
          </div>
        )}
        <div>
          <p className="font-semibold text-slate-900">{review.name}</p>
          {review.position && (
            <p className="text-sm text-slate-500">{review.position}</p>
          )}
        </div>
        {review.source === 'Google' && (
          <img 
            src="https://www.google.com/favicon.ico" 
            alt="Google" 
            className="w-5 h-5 ml-auto"
          />
        )}
      </div>
    </CardContent>
  </Card>
);

export const ReviewsShowcase = ({ 
  maxReviews = 6,
  showVideoTestimonials = true,
  showTitle = true,
  className = ""
}) => {
  const [reviews, setReviews] = useState([]);
  const [videoReviews, setVideoReviews] = useState([]);
  const [clientLogos, setClientLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [videoModal, setVideoModal] = useState({ open: false, review: null });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reviewsRes, logosRes] = await Promise.all([
          axios.get(`${API}/reviews/homepage`),
          axios.get(`${API}/client-logos`)
        ]);
        
        const allReviews = reviewsRes.data;
        setVideoReviews(allReviews.filter(r => r.is_video_testimonial).slice(0, 4));
        setReviews(allReviews.filter(r => !r.is_video_testimonial).slice(0, maxReviews));
        setClientLogos(logosRes.data);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [maxReviews]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % Math.max(1, reviews.length - 2));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + Math.max(1, reviews.length - 2)) % Math.max(1, reviews.length - 2));
  };

  if (loading) {
    return (
      <section className={`py-20 bg-slate-50 ${className}`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-64 bg-slate-200 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (reviews.length === 0 && videoReviews.length === 0) {
    return null;
  }

  return (
    <>
      {/* Main Reviews Section */}
      <section className={`py-20 bg-slate-50 ${className}`} data-testid="reviews-showcase">
        <div className="max-w-7xl mx-auto px-4">
          {showTitle && (
            <div className="text-center mb-12">
              <p className="text-orange-500 font-semibold mb-2 flex items-center justify-center gap-2">
                <BadgeCheck className="w-5 h-5" /> CLIENT TESTIMONIALS
              </p>
              <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Hear from Our Satisfied Clients
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-sky-500 to-orange-500 rounded-full mx-auto mb-4"></div>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Don't just take our word for it. See what our customers say about our quality work and service.
              </p>
            </div>
          )}

          {/* Video Testimonials Row */}
          {showVideoTestimonials && videoReviews.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Play className="w-5 h-5 text-red-500" /> Video Testimonials
                </h3>
                {videoReviews.length > 3 && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={prevSlide} className="rounded-full">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={nextSlide} className="rounded-full">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {videoReviews.map((review) => (
                  <VideoTestimonialCard 
                    key={review.id} 
                    review={review}
                    onClick={() => setVideoModal({ open: true, review })}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Text Reviews Grid */}
          {reviews.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {reviews.slice(0, 6).map((review) => (
                <TextReviewCard key={review.id} review={review} />
              ))}
            </div>
          )}

          {/* CTA Button */}
          <div className="text-center">
            <Link to="/reviews">
              <Button size="lg" className="bg-sky-600 hover:bg-sky-700" data-testid="read-more-reviews-btn">
                Read More Reviews <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Clients Trust Us - Logos */}
      {clientLogos.length > 0 && (
        <section className="py-12 bg-white border-y border-slate-200" data-testid="client-logos-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-8">
              <p className="text-slate-500 font-medium uppercase tracking-wider text-sm">
                Why Clients Trust Us
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
              {clientLogos.map((logo) => (
                <div 
                  key={logo.id}
                  className="grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300"
                  data-testid={`client-logo-${logo.id}`}
                >
                  {logo.website ? (
                    <a href={logo.website} target="_blank" rel="noopener noreferrer">
                      <img 
                        src={logo.logo_url} 
                        alt={logo.name}
                        className="h-12 md:h-16 w-auto object-contain"
                      />
                    </a>
                  ) : (
                    <img 
                      src={logo.logo_url} 
                      alt={logo.name}
                      className="h-12 md:h-16 w-auto object-contain"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Video Modal */}
      <Dialog open={videoModal.open} onOpenChange={(open) => !open && setVideoModal({ open: false, review: null })}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden bg-black border-0">
          {videoModal.review && (
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 z-10 text-white hover:bg-white/20"
                onClick={() => setVideoModal({ open: false, review: null })}
              >
                <X className="w-5 h-5" />
              </Button>

              {videoModal.review.video_type === 'youtube' && (
                <div className="aspect-video w-full">
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYoutubeId(videoModal.review.video_url)}?autoplay=1`}
                    className="w-full h-full"
                    frameBorder="0"
                    allowFullScreen
                    allow="autoplay; encrypted-media"
                  />
                </div>
              )}

              {videoModal.review.video_type === 'instagram' && (
                <div className="aspect-[9/16] w-full max-h-[80vh]">
                  <iframe
                    src={`https://www.instagram.com/reel/${extractInstagramId(videoModal.review.video_url)}/embed`}
                    className="w-full h-full"
                    frameBorder="0"
                    allowFullScreen
                  />
                </div>
              )}

              {videoModal.review.video_type === 'direct' && (
                <video 
                  src={videoModal.review.video_url} 
                  controls 
                  autoPlay
                  className="w-full"
                />
              )}

              <div className="p-4 bg-slate-900">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-sky-600 rounded-full flex items-center justify-center text-white font-bold">
                    {videoModal.review.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-white font-semibold">{videoModal.review.name}</p>
                    {videoModal.review.position && (
                      <p className="text-slate-400 text-sm">{videoModal.review.position}</p>
                    )}
                  </div>
                  <div className="ml-auto">
                    <StarRating rating={videoModal.review.rating} size="sm" />
                  </div>
                </div>
                {videoModal.review.review && (
                  <p className="text-slate-300 text-sm mt-3 line-clamp-3">"{videoModal.review.review}"</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReviewsShowcase;
